import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { motion } from 'framer-motion';
import React, { memo, useMemo } from 'react';
import formLocalizedHelperText from 'utils/formLocalizedHelperText';
import { fadeUp } from 'utils/motion';

const FormSimpleSelectField = ({
  delay = 0,
  duration = 0.5,
  options = [],
  formik,
  fieldName,
  label,
  readOnly = false,
  disabled = false,
  withNestedField = false,
  itemValue = 'id',
  itemLabel = 'name',
  changeFn = null,
  variant = 'outlined',
  size = 'medium',
  ...props
}) => {
  const getFieldValue = useMemo(() => {
    if (fieldName && fieldName.includes('.')) {
      let a = fieldName.split('.');
      let b = formik.values;
      for (let i = 0; i < a.length; i++) {
        b = b?.[a?.[i]];
      }
      return b || '';
    }
    return formik.values?.[fieldName] || '';
  }, [formik.values, fieldName]);

  const isNotInOptions = useMemo(() => {
    return options?.find((opt) => opt?.[itemValue] == getFieldValue)
      ? true
      : false;
  }, [options, itemValue, getFieldValue]);

  return (
    <FormControl
      fullWidth
      component={motion.div}
      variants={fadeUp(30, 'tween', delay, duration)}
      initial='hidden'
      animate='show'
      viewport={{ once: true, amount: 0.25 }}
      color='formColor'
      size={size}
      error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
    >
      <InputLabel id={`${fieldName}-label`}>{label}</InputLabel>
      <Select
        labelId={`${fieldName}-label`}
        id={`${fieldName}-select`}
        label={label}
        onChange={(event) => {
          if (changeFn && typeof changeFn == 'function') {
            changeFn(event.target.value);
          } else {
            formik.setFieldValue(fieldName, event.target.value, true);
          }
        }}
        value={withNestedField ? getFieldValue : formik.values[fieldName]}
        color='formColor'
        variant={variant}
        readOnly={readOnly}
        disabled={disabled}
        role='presentation'
        size={size}
        MenuProps={{
          id: `${fieldName}-select-menu`,
          disableScrollLock: true,
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
        {...props}
      >
        {!isNotInOptions && (
          <MenuItem
            value={getFieldValue}
            key={getFieldValue}
            sx={{ display: 'none' }}
          >
            {getFieldValue}
          </MenuItem>
        )}
        {options.map((item, index) => (
          <MenuItem value={item[itemValue]} key={index + 1}>
            {item[itemLabel]}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText
        children={
          <span>
            {formik.touched[fieldName] &&
              formLocalizedHelperText(formik.errors[fieldName])}
          </span>
        }
        error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
      />
    </FormControl>
  );
};

export default memo(FormSimpleSelectField);
